import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import CustomTable, { ColumnConfig } from "components/CustomTable";
import Pagination from "components/Pagination";
import StatusBadge from "components/admin/StatusBadge";
import { Input } from "components/ui/input";
import {
  AdminClienteListItem,
  listAdminClientes,
} from "services/admin/adminClientes.service";

const STATUS_TONE: Record<string, "green" | "yellow" | "red"> = {
  ativo: "green",
  suspenso: "yellow",
  inativo: "red",
};

type Row = {
  empresa: React.ReactNode;
  responsavel: string;
  plano: string;
  status: React.ReactNode;
  cadastro: string;
  shopify: React.ReactNode;
  nuvemshop: React.ReactNode;
  ultimaSync: string;
  ultimoAcesso: string;
  id: number;
};

const columns: ColumnConfig<Row>[] = [
  { key: "empresa", header: "Empresa" },
  { key: "responsavel", header: "Responsável" },
  { key: "plano", header: "Plano" },
  { key: "status", header: "Status" },
  { key: "cadastro", header: "Cadastro" },
  { key: "shopify", header: "Shopify" },
  { key: "nuvemshop", header: "Nuvemshop" },
  { key: "ultimaSync", header: "Última sinc." },
  { key: "ultimoAcesso", header: "Último acesso" },
];

const AdminClientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<AdminClienteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [status, setStatus] = useState("");
  const [plano, setPlano] = useState("");
  const [plataforma, setPlataforma] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    listAdminClientes({ page, pageSize: 20, status, plano, plataforma, search })
      .then((resp) => {
        setClientes(resp.data);
        setTotalPages(resp.pagination.totalPages || 1);
      })
      .catch((err) => console.error("[AdminClientes] erro ao listar:", err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, plano, plataforma, search]);

  const rows: Row[] = clientes.map((c) => ({
    id: c.id,
    empresa: (
      <button className="text-brand-primary font-semibold hover:underline" onClick={() => navigate(`/admin/clientes/${c.id}`)}>
        {c.razaoSocial}
      </button>
    ),
    responsavel: c.emailPrincipal,
    plano: c.plano,
    status: <StatusBadge label={c.status} tone={STATUS_TONE[c.status] || "slate"} />,
    cadastro: new Date(c.createdAt).toLocaleDateString("pt-BR"),
    shopify: <StatusBadge label={c.shopifyConectado ? "Conectado" : "—"} tone={c.shopifyConectado ? "blue" : "slate"} />,
    nuvemshop: <StatusBadge label={c.nuvemshopConectado ? "Conectado" : "—"} tone={c.nuvemshopConectado ? "blue" : "slate"} />,
    ultimaSync: c.ultimaSincronizacao ? new Date(c.ultimaSincronizacao).toLocaleString("pt-BR") : "—",
    ultimoAcesso: c.lastLoginAt ? new Date(c.lastLoginAt).toLocaleString("pt-BR") : "—",
  }));

  return (
    <AdminLayout title="Clientes" subtitle="Gestão de clientes da plataforma">
      <div className="flex flex-col gap-4">
        <AdminCard noPadding className="flex flex-wrap gap-3 items-end p-4">
          <Input
            placeholder="Buscar por empresa, e-mail ou CNPJ"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            className="max-w-xs"
          />
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={status}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
            <option value="suspenso">Suspenso</option>
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={plano}
            onChange={(e) => { setPage(1); setPlano(e.target.value); }}
          >
            <option value="">Todos os planos</option>
            <option value="basico">Básico</option>
            <option value="gold">Gold</option>
            <option value="premium">Premium</option>
            <option value="minimo">Mínimo</option>
            <option value="vinte">Vinte</option>
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={plataforma}
            onChange={(e) => { setPage(1); setPlataforma(e.target.value); }}
          >
            <option value="">Todas as plataformas</option>
            <option value="shopify">Shopify</option>
            <option value="nuvemshop">Nuvemshop</option>
          </select>
        </AdminCard>

        <AdminCard noPadding className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : (
            <>
              <CustomTable objectList={rows} columns={columns} />
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default AdminClientes;
